/**
 * Grok (xAI) provider adapter for ludicrous-gateway
 * Connects to xAI's OpenAI-compatible API
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig, ApiKeyConfig } from "../types/config.js";
import type { TextContent } from "../types/messages.js";

/**
 * Available Grok models
 */
export const GROK_MODELS = [
  { value: "grok-2", label: "Grok 2 (Latest)" },
  { value: "grok-2-mini", label: "Grok 2 Mini (Faster)" },
  { value: "grok-beta", label: "Grok Beta" },
] as const;

/**
 * Grok OpenAI-compatible request format
 */
interface GrokRequest {
  model: string;
  messages: Array<{
    role: "system" | "user" | "assistant";
    content: string;
  }>;
  stream: boolean;
  temperature?: number | undefined;
  top_p?: number | undefined;
  max_tokens?: number | undefined;
  stop?: string[] | undefined;
}

/**
 * Grok response format (OpenAI-compatible)
 */
interface GrokResponse {
  id: string;
  object: "chat.completion";
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: "assistant";
      content: string;
    };
    finish_reason: "stop" | "length" | null;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * Grok streaming chunk format
 */
interface GrokStreamChunk {
  id: string;
  object: "chat.completion.chunk";
  created: number;
  model: string;
  choices: Array<{
    index: number;
    delta: {
      role?: "assistant";
      content?: string;
    };
    finish_reason: "stop" | "length" | null;
  }>;
}

/**
 * Grok (xAI) API provider
 */
export class GrokProvider extends BaseProvider {
  private apiKeys: ApiKeyConfig[];
  private currentKeyIndex: number = 0;

  constructor(config: ProviderConfig) {
    super(config);
    this.apiKeys = config.keys ?? [];

    if (this.apiKeys.length === 0) {
      this.logger.warn("No API keys configured for Grok provider");
    }
  }

  protected getDefaultBaseUrl(): string {
    return "https://api.x.ai";
  }

  /**
   * Get the next API key (round-robin)
   */
  private getNextApiKey(): string | null {
    if (this.apiKeys.length === 0) {
      return null;
    }

    const key = this.apiKeys[this.currentKeyIndex];
    this.currentKeyIndex = (this.currentKeyIndex + 1) % this.apiKeys.length;
    return key?.key ?? null;
  }

  /**
   * Build headers for Grok API
   */
  protected override buildHeaders(
    _context: ProviderRequestContext
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const apiKey = this.getNextApiKey();
    if (apiKey) {
      headers["Authorization"] = `Bearer ${apiKey}`;
    }

    return headers;
  }

  /**
   * Convert Anthropic messages to Grok (OpenAI) format
   */
  private convertToGrokFormat(
    request: MessagesRequest,
    resolvedModel: string
  ): GrokRequest {
    const messages: GrokRequest["messages"] = [];

    // Add system prompt if present
    if (request.system) {
      const systemText =
        typeof request.system === "string"
          ? request.system
          : request.system
              .filter((b): b is TextContent => b.type === "text")
              .map((b) => b.text)
              .join("\n");

      messages.push({
        role: "system",
        content: systemText,
      });
    }

    // Convert messages
    for (const msg of request.messages) {
      let content: string;

      if (typeof msg.content === "string") {
        content = msg.content;
      } else {
        content = msg.content
          .filter((b): b is TextContent => b.type === "text")
          .map((b) => b.text)
          .join("\n");
      }

      messages.push({
        role: msg.role,
        content,
      });
    }

    return {
      model: resolvedModel,
      messages,
      stream: request.stream ?? false,
      temperature: request.temperature,
      top_p: request.top_p,
      max_tokens: request.max_tokens,
      stop: request.stop_sequences,
    };
  }

  /**
   * Convert Grok response to Anthropic format
   */
  private convertFromGrokFormat(
    response: GrokResponse,
    model: string
  ): MessagesResponse {
    const choice = response.choices[0];

    return {
      id: response.id,
      type: "message",
      role: "assistant",
      content: [
        {
          type: "text",
          text: choice?.message?.content ?? "",
        },
      ],
      model,
      stop_reason:
        choice?.finish_reason === "stop"
          ? "end_turn"
          : choice?.finish_reason === "length"
            ? "max_tokens"
            : "end_turn",
      stop_sequence: null,
      usage: {
        input_tokens: response.usage.prompt_tokens,
        output_tokens: response.usage.completion_tokens,
      },
    };
  }

  /**
   * Send a non-streaming request to Grok
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/chat/completions`;
      const headers = this.buildHeaders(context);
      const grokRequest = this.convertToGrokFormat(
        request,
        context.resolvedModel
      );
      grokRequest.stream = false;

      this.logger.debug("Sending request to Grok", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(grokRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Grok API error: ${response.status} - ${errorText}`);
      }

      const data = (await response.json()) as GrokResponse;
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("Grok request successful", { latencyMs });

      return this.convertFromGrokFormat(data, context.resolvedModel);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("Grok request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to Grok
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/chat/completions`;
      const headers = this.buildHeaders(context);
      headers["Accept"] = "text/event-stream";

      const grokRequest = this.convertToGrokFormat(
        request,
        context.resolvedModel
      );
      grokRequest.stream = true;

      this.logger.debug("Starting streaming request to Grok", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(grokRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Grok API error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Send initial message_start event (Anthropic SSE format)
      handler.onEvent(
        "message_start",
        JSON.stringify({
          type: "message_start",
          message: {
            id: `grok-${Date.now()}`,
            type: "message",
            role: "assistant",
            content: [],
            model: context.resolvedModel,
            stop_reason: null,
            stop_sequence: null,
            usage: { input_tokens: 0, output_tokens: 0 },
          },
        })
      );

      // Send content_block_start
      handler.onEvent(
        "content_block_start",
        JSON.stringify({
          type: "content_block_start",
          index: 0,
          content_block: { type: "text", text: "" },
        })
      );

      // Process SSE stream from Grok
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;

          const data = line.slice(6);
          if (data === "[DONE]") {
            // Send final events
            handler.onEvent(
              "content_block_stop",
              JSON.stringify({
                type: "content_block_stop",
                index: 0,
              })
            );

            handler.onEvent(
              "message_delta",
              JSON.stringify({
                type: "message_delta",
                delta: {
                  stop_reason: "end_turn",
                  stop_sequence: null,
                },
                usage: {
                  output_tokens: 0, // Grok doesn't provide token count in streaming
                },
              })
            );

            handler.onEvent(
              "message_stop",
              JSON.stringify({
                type: "message_stop",
              })
            );
            continue;
          }

          try {
            const chunk = JSON.parse(data) as GrokStreamChunk;
            const content = chunk.choices[0]?.delta?.content;

            if (content) {
              handler.onEvent(
                "content_block_delta",
                JSON.stringify({
                  type: "content_block_delta",
                  index: 0,
                  delta: {
                    type: "text_delta",
                    text: content,
                  },
                })
              );
            }
          } catch {
            // Skip malformed chunks
            this.logger.debug("Skipping malformed Grok response chunk");
          }
        }
      }

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("Grok streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("Grok streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }
}

/**
 * Create a Grok provider from config
 */
export function createGrokProvider(config: ProviderConfig): GrokProvider {
  return new GrokProvider(config);
}
